/**
 * 2D Canvas for warehouse layout editing.
 * Uses context for state, composable renderers for drawing, and conditional animation loop.
 */

import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { X } from 'lucide-react';
import { useLayoutEditor } from '../contexts/LayoutEditorContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import CalibrationDialog from './CalibrationDialog';
import InsightLegend from './InsightLegend';
import ZoneSummaryPanel from './ZoneSummaryPanel';
import MoveTargetFilterPanel, {
  MoveTargetFilters,
  DEFAULT_MOVE_FILTERS,
} from './MoveTargetFilterPanel';
import {
  warehouseLayoutService,
  ServerMoveTargets,
} from '../../../../../services/warehouseLayoutService';
import {
  RackPosition,
  LocationActivity,
  WarehouseObject,
} from '../types/warehouseLayout';
import {
  OBJECT_TYPE_DEFAULT_SIZES,
  generateObjectId,
  objectTypeLabel,
} from '../utils/objectTools';
import {
  snapToGrid,
  screenToWorld,
  getRotatedDimensions,
} from '../utils/positionCalculator';
import {
  isMultiSelectKey,
  isRangeSelectKey,
  getRacksInArea,
  toggleSelection,
  singleSelect,
  rangeSelect,
  startRubberBand,
  updateRubberBand,
  resetRubberBand,
} from '../utils/selectionUtils';
import {
  computeRenderContext,
  drawBackground,
  drawFloor,
  drawGrid,
  drawBackgroundImage,
  drawDimensionLabels,
  drawObjects,
  drawBinPositions,
  drawPreviewRacks,
  drawRacks,
  drawPendingMoves,
  drawRubberBand,
  drawOverlays,
  drawCalibration,
  PendingMovePointResolver,
} from '../utils/canvasRenderers';
import { resolveBinRenderInfo, PlacedBinRenderInfo } from '../utils/layoutApiMapping';
import { buildRackIntensity, buildBinValueLookup } from '../utils/insightUtils';

interface DragState {
  isDragging: boolean;
  draggedRacks: string[];
  startPositions: Map<string, { x: number; y: number }>;
  mouseStartX: number;
  mouseStartY: number;
}

/** Drag-tilstand for enkeltlokasjoner (BinPositions) — separat fra reol-drag */
interface BinDragState {
  isDragging: boolean;
  binCode: string | null;
  startX: number;
  startY: number;
  mouseStartX: number;
  mouseStartY: number;
}

const IDLE_BIN_DRAG: BinDragState = {
  isDragging: false,
  binCode: null,
  startX: 0,
  startY: 0,
  mouseStartX: 0,
  mouseStartY: 0,
};

/** Drag-tilstand for objekter (søyle/port/stasjon/lading/sonemarkør) */
interface ObjectDragState {
  isDragging: boolean;
  objectId: string | null;
  startX: number;
  startY: number;
  mouseStartX: number;
  mouseStartY: number;
}

const IDLE_OBJECT_DRAG: ObjectDragState = {
  isDragging: false,
  objectId: null,
  startX: 0,
  startY: 0,
  mouseStartX: 0,
  mouseStartY: 0,
};

/** Norsk tallformat for antall i reol-popoveren (flytte-modus) */
const moveQuantityFormat = new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 2 });

/** Popover for valg av kandidat-bin i en reol (flytte-modus) — skjermkoordinater i canvas */
interface MovePopoverState {
  rackNumber: string;
  x: number;
  y: number;
}

const Layout2DCanvas: React.FC = () => {
  const { state, dispatch, rackDimensionsMap, backgroundUrl, warehouseId } = useLayoutEditor();
  const { t } = useTranslation();
  const { layout, editorState, previewPositions } = state;

  // Plantegning-underlag: last bilde-element når URL endres
  const [backgroundImage, setBackgroundImage] = useState<HTMLImageElement | null>(null);
  useEffect(() => {
    if (!backgroundUrl) {
      setBackgroundImage(null);
      return;
    }
    const img = new Image();
    img.onload = () => setBackgroundImage(img);
    img.src = backgroundUrl;
  }, [backgroundUrl]);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    draggedRacks: [],
    startPositions: new Map(),
    mouseStartX: 0,
    mouseStartY: 0,
  });
  const [binDragState, setBinDragState] = useState<BinDragState>(IDLE_BIN_DRAG);
  const [objectDragState, setObjectDragState] = useState<ObjectDragState>(IDLE_OBJECT_DRAG);
  // Musens world-posisjon under kalibrering — for «gummistrikk»-linjen fra punkt A
  const [calibrationHover, setCalibrationHover] = useState<{ x: number; y: number } | null>(null);

  // Rydd gummistrikk-punktet når kalibreringen avsluttes/avbrytes
  useEffect(() => {
    if (!state.calibration.active) setCalibrationHover(null);
  }, [state.calibration.active]);

  // --- «Flytt herfra»-flyt: mål-velger-modus med filtrerte kandidater ---
  const moveState = state.moveState;
  const moveActive = !!moveState;
  const moveFromBinCode = moveState?.fromBinCode ?? null;
  const [moveFilters, setMoveFilters] = useState<MoveTargetFilters>(DEFAULT_MOVE_FILTERS);
  const [moveTargets, setMoveTargets] = useState<ServerMoveTargets | null>(null);
  const [moveTargetsLoading, setMoveTargetsLoading] = useState(false);
  const [moveTargetsError, setMoveTargetsError] = useState<string | null>(null);
  const [movePopover, setMovePopover] = useState<MovePopoverState | null>(null);

  // Nullstill filtre/popover når flytte-modusen starter eller avsluttes
  useEffect(() => {
    setMoveFilters(DEFAULT_MOVE_FILTERS);
    setMovePopover(null);
    if (!moveFromBinCode) {
      setMoveTargets(null);
      setMoveTargetsError(null);
      setMoveTargetsLoading(false);
    }
  }, [moveFromBinCode]);

  // Hent kandidat-mål ved filterendring (250 ms debounce) — kildelokasjonen ekskluderes
  useEffect(() => {
    if (!moveFromBinCode) return;
    let cancelled = false;
    setMoveTargetsLoading(true);
    const timer = setTimeout(() => {
      warehouseLayoutService.getMoveTargets(warehouseId, {
        emptyOnly: moveFilters.emptyOnly,
        pickableOnly: moveFilters.pickableOnly,
        zoneId: moveFilters.zoneId || undefined,
        binTypeNo: moveFilters.binTypeNo ? Number(moveFilters.binTypeNo) : undefined,
        binSizeNo: moveFilters.binSizeNo ? Number(moveFilters.binSizeNo) : undefined,
        excludeBinCode: moveFromBinCode,
      })
        .then(result => {
          if (cancelled) return;
          setMoveTargets(result);
          setMoveTargetsError(null);
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          setMoveTargets(null);
          setMoveTargetsError(err instanceof Error ? err.message : String(err));
        })
        .finally(() => {
          if (!cancelled) setMoveTargetsLoading(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [moveFromBinCode, moveFilters, warehouseId]);

  // Kandidat-enkeltlokasjoner (grønn ramme i canvas)
  const moveCandidateBinSet = useMemo(() => {
    if (!moveActive || !moveTargets) return null;
    return new Set(moveTargets.bins.map(bin => bin.binCode));
  }, [moveActive, moveTargets]);

  // Antall kandidat-bins per reol (grønn ramme + badge i canvas)
  const moveCandidateRackCounts = useMemo(() => {
    if (!moveActive || !moveTargets) return null;
    const counts = new Map<string, number>();
    moveTargets.bins.forEach(bin => {
      if (bin.rackNumber) counts.set(bin.rackNumber, (counts.get(bin.rackNumber) ?? 0) + 1);
    });
    return counts;
  }, [moveActive, moveTargets]);

  // Kandidat-bins i reolen popoveren gjelder (naturlig sortert på binCode)
  const movePopoverBins = useMemo(() => {
    if (!movePopover || !moveTargets) return [];
    return moveTargets.bins
      .filter(bin => bin.rackNumber === movePopover.rackNumber)
      .sort((a, b) => a.binCode.localeCompare(b.binCode, undefined, { numeric: true }));
  }, [movePopover, moveTargets]);

  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [lastClickedRack, setLastClickedRack] = useState<string | null>(null);

  // Stub data (Phase 2 integration)
  const activities: LocationActivity[] = [];

  // Enkeltlokasjoner (gulv/vegg/heng/skap/dock) med mål fra StcBinSize
  const binRenderInfos = useMemo(() => {
    const specialByCode = new Map(state.specialBins.map(b => [b.binCode, b]));
    return state.binPositions.map(p => resolveBinRenderInfo(p, specialByCode));
  }, [state.binPositions, state.specialBins]);

  // Innsikt-overlay: aggregert intensitet per reol + verdi per enkeltlokasjon
  const insightActive = state.insightMode !== 'none';
  const heatmapLookup = useMemo(
    () => (insightActive ? buildRackIntensity(state.insights) : new Map<string, number>()),
    [insightActive, state.insights]
  );
  const binValueLookup = useMemo(
    () => (insightActive ? buildBinValueLookup(state.insights) : null),
    [insightActive, state.insights]
  );

  // Ventende flytt: binCode → plassert enkeltlokasjons senter; ellers reolens
  // senter (rackNumber fra DTO); ingen av delene → null (flyttet hoppes over)
  const pendingMoveResolver = useMemo<PendingMovePointResolver>(() => {
    const binPoints = new Map<string, { x: number; y: number }>();
    binRenderInfos.forEach(bin => binPoints.set(bin.binCode, { x: bin.x, y: bin.y }));
    const rackPoints = new Map<string, { x: number; y: number }>();
    (layout?.racks ?? []).forEach(rack => rackPoints.set(rack.rackNumber, { x: rack.x, y: rack.y }));
    return (binCode, rackNumber) =>
      binPoints.get(binCode) ?? (rackNumber ? rackPoints.get(rackNumber) ?? null : null);
  }, [binRenderInfos, layout?.racks]);

  const activityLookup = useMemo(() => {
    const lookup = new Map<string, LocationActivity[]>();
    activities.forEach(activity => {
      const key = activity.rackNumber;
      if (!lookup.has(key)) lookup.set(key, []);
      lookup.get(key)!.push(activity);
    });
    return lookup;
  }, [activities]);

  // Handle canvas resize
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setCanvasSize({ width: rect.width, height: rect.height });
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    if (!layout) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ikke stjel tastetrykk fra skjemafelter (egenskaps-panel, søk, innstillinger)
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return;
      }

      // Undo (Ctrl+Z)
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        dispatch({ type: 'UNDO' });
        return;
      }

      // Redo (Ctrl+Shift+Z or Ctrl+Y)
      if ((e.ctrlKey || e.metaKey) && (e.key === 'Z' || e.key === 'y') && (e.shiftKey || e.key === 'y')) {
        e.preventDefault();
        dispatch({ type: 'REDO' });
        return;
      }

      // Flytte-modus: Esc avbryter (samme mønster som kalibrering); zoom/pan-reset
      // virker fortsatt, men alle redigerings-snarveier er av. MoveConfirmDialog
      // fanger Esc selv (capture + stopPropagation) når den er åpen.
      if (moveActive) {
        if (e.key === 'Escape') {
          setMovePopover(null);
          dispatch({ type: 'CANCEL_MOVE' });
        } else if (e.key === '+' || e.key === '=') {
          dispatch({ type: 'SET_ZOOM', zoom: Math.min(5, editorState.zoom * 1.1) });
        } else if (e.key === '-') {
          dispatch({ type: 'SET_ZOOM', zoom: Math.max(0.3, editorState.zoom * 0.9) });
        } else if (e.key === 'h') {
          dispatch({ type: 'SET_ZOOM', zoom: 1 });
          dispatch({ type: 'SET_PAN_OFFSET', offset: { x: 0, y: 0 } });
        }
        return;
      }

      // Objektplasseringsmodus: Esc avslutter (samme mønster som flytte-modus);
      // zoom/pan-reset virker fortsatt, øvrige redigerings-snarveier er av.
      if (state.objectPlacement) {
        if (e.key === 'Escape') {
          dispatch({ type: 'CANCEL_OBJECT_PLACEMENT' });
        } else if (e.key === '+' || e.key === '=') {
          dispatch({ type: 'SET_ZOOM', zoom: Math.min(5, editorState.zoom * 1.1) });
        } else if (e.key === '-') {
          dispatch({ type: 'SET_ZOOM', zoom: Math.max(0.3, editorState.zoom * 0.9) });
        } else if (e.key === 'h') {
          dispatch({ type: 'SET_ZOOM', zoom: 1 });
          dispatch({ type: 'SET_PAN_OFFSET', offset: { x: 0, y: 0 } });
        }
        return;
      }

      // Select all (Ctrl+A)
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault();
        dispatch({ type: 'SET_SELECTION', selectedRacks: new Set(layout.racks.map(r => r.rackNumber)) });
      }

      // Deselect all (Escape)
      if (e.key === 'Escape') {
        if (state.calibration.active) {
          dispatch({ type: 'CANCEL_CALIBRATION' });
          setCalibrationHover(null);
          return;
        }
        dispatch({ type: 'SET_SELECTION', selectedRacks: new Set() });
        if (state.selectedBinCode) {
          dispatch({ type: 'SET_SELECTED_BIN', binCode: null });
        }
        if (editorState.selectedObject) {
          dispatch({ type: 'SET_SELECTED_OBJECT', id: null });
        }
        if (editorState.rubberBand.isActive) {
          dispatch({ type: 'SET_RUBBER_BAND', rubberBand: resetRubberBand() });
        }
      }

      // Objekt valgt: R = roter 90°, Delete = fjern objektet
      if (editorState.selectedObject) {
        const selectedObj = layout.objects.find(o => o.id === editorState.selectedObject);
        if (e.key === 'r' && !e.ctrlKey && !e.metaKey && selectedObj) {
          dispatch({
            type: 'UPDATE_OBJECT',
            id: selectedObj.id,
            changes: { rotation: ((selectedObj.rotation ?? 0) + 90) % 360 },
          });
          return;
        }
        if (e.key === 'Delete') {
          dispatch({ type: 'REMOVE_OBJECT', id: editorState.selectedObject });
          return;
        }
      }

      // Enkeltlokasjon valgt: R = roter, Delete = fjern plasseringen (ikke lokasjonen)
      if (state.selectedBinCode) {
        if (e.key === 'r' && !e.ctrlKey && !e.metaKey) {
          dispatch({ type: 'ROTATE_BIN', binCode: state.selectedBinCode });
          return;
        }
        if (e.key === 'Delete') {
          dispatch({ type: 'REMOVE_BIN', binCode: state.selectedBinCode });
          return;
        }
      }

      // Delete selected (Delete)
      if (e.key === 'Delete' && editorState.selectedRacks.size > 0) {
        dispatch({ type: 'DELETE_SELECTED' });
      }

      // Rotate selected (R)
      if (e.key === 'r' && editorState.selectedRacks.size > 0 && !e.ctrlKey && !e.metaKey) {
        dispatch({ type: 'ROTATE_SELECTED' });
      }

      // Align to grid (G)
      if (e.key === 'g' && editorState.selectedRacks.size > 0 && !e.ctrlKey && !e.metaKey) {
        dispatch({ type: 'ALIGN_SELECTED_TO_GRID' });
      }

      // Arrow keys to nudge selected racks
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key) && editorState.selectedRacks.size > 0) {
        e.preventDefault();
        const step = layout.gridUnit;
        const updates: Array<{ rackNumber: string; x: number; y: number }> = [];
        layout.racks.forEach(rack => {
          if (editorState.selectedRacks.has(rack.rackNumber)) {
            let dx = 0, dy = 0;
            if (e.key === 'ArrowLeft') dx = -step;
            if (e.key === 'ArrowRight') dx = step;
            if (e.key === 'ArrowUp') dy = -step;
            if (e.key === 'ArrowDown') dy = step;
            updates.push({
              rackNumber: rack.rackNumber,
              x: snapToGrid(rack.x + dx, layout.gridUnit),
              y: snapToGrid(rack.y + dy, layout.gridUnit),
            });
          }
        });
        // Push history for arrow nudge, then move
        dispatch({ type: 'MOVE_RACKS', updates });
      }

      // Zoom with + / -
      if (e.key === '+' || e.key === '=') {
        dispatch({ type: 'SET_ZOOM', zoom: Math.min(5, editorState.zoom * 1.1) });
      }
      if (e.key === '-') {
        dispatch({ type: 'SET_ZOOM', zoom: Math.max(0.3, editorState.zoom * 0.9) });
      }

      // Reset view (H)
      if (e.key === 'h' && !e.ctrlKey && !e.metaKey) {
        dispatch({ type: 'SET_ZOOM', zoom: 1 });
        dispatch({ type: 'SET_PAN_OFFSET', offset: { x: 0, y: 0 } });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editorState, layout, dispatch, state.selectedBinCode, state.calibration.active, moveActive, state.objectPlacement]);

  // Draw canvas using composable renderers
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !layout) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rc = computeRenderContext(
      ctx, canvasSize.width, canvasSize.height,
      layout.dimensions, editorState.zoom, editorState.panOffset
    );

    drawBackground(rc);
    drawFloor(rc, layout.dimensions);
    if (backgroundImage) drawBackgroundImage(rc, backgroundImage, layout.dimensions);
    if (editorState.showGrid) drawGrid(rc, layout.dimensions, layout.gridUnit);
    drawDimensionLabels(rc, layout.dimensions);
    drawObjects(rc, layout.objects, editorState.showLabels, editorState.selectedObject);
    drawBinPositions(rc, binRenderInfos, editorState.showLabels, state.selectedBinCode, binValueLookup, moveCandidateBinSet);
    // Render bulk preview positions + sidebar drag ghost
    const allPreviews = state.sidebarDragPreview
      ? [...previewPositions, state.sidebarDragPreview]
      : previewPositions;
    drawPreviewRacks(rc, allPreviews, rackDimensionsMap);
    drawRacks(
      rc, layout.racks, rackDimensionsMap,
      editorState.selectedRacks, editorState.anchorRack,
      editorState.showLabels, insightActive, editorState.showActivity,
      heatmapLookup, activityLookup, moveCandidateRackCounts
    );
    // Ventende flytt tegnes ETTER reolene (øverst) — respekterer togglen
    if (state.showPendingMoves && state.pendingMoves.length > 0) {
      drawPendingMoves(rc, state.pendingMoves, pendingMoveResolver);
    }
    drawRubberBand(rc, editorState.rubberBand);
    if (state.calibration.active) {
      drawCalibration(rc, state.calibration.pointA, state.calibration.pointB, calibrationHover);
    }
    drawOverlays(rc, rc.scale);
  }, [layout, editorState, canvasSize, previewPositions, rackDimensionsMap, insightActive, heatmapLookup, binValueLookup, activityLookup, binRenderInfos, backgroundImage, state.selectedBinCode, state.calibration, calibrationHover, state.sidebarDragPreview, moveCandidateBinSet, moveCandidateRackCounts, state.showPendingMoves, state.pendingMoves, pendingMoveResolver]);

  // Redraw on changes - conditional animation loop
  useEffect(() => {
    draw();

    const hasActiveAnimations = editorState.showActivity && activities.length > 0;
    if (!hasActiveAnimations) return;

    let animationId: number;
    let lastFrameTime = 0;
    const targetFps = 30;
    const frameInterval = 1000 / targetFps;

    const animate = (timestamp: number) => {
      if (timestamp - lastFrameTime >= frameInterval) {
        draw();
        lastFrameTime = timestamp;
      }
      animationId = requestAnimationFrame(animate);
    };
    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, [draw, editorState.showActivity, activities.length]);

  // Get rack at screen position
  const getRackAtPosition = (screenX: number, screenY: number): RackPosition | null => {
    if (!layout) return null;
    const world = screenToWorld(
      screenX, screenY,
      canvasSize.width, canvasSize.height,
      layout.dimensions, editorState.zoom, editorState.panOffset
    );

    for (const rack of layout.racks) {
      const dim = rackDimensionsMap.get(rack.rackNumber) || { width: 6.8, depth: 1.3, height: 2.7, verticals: 10, shelves: 5 };
      const rotated = getRotatedDimensions(dim, rack.rotation);
      if (
        world.x >= rack.x - rotated.width / 2 &&
        world.x <= rack.x + rotated.width / 2 &&
        world.y >= rack.y - rotated.depth / 2 &&
        world.y <= rack.y + rotated.depth / 2
      ) {
        return rack;
      }
    }
    return null;
  };

  // Hit-test for enkeltlokasjoner — samme world-koordinat-logikk som reolene.
  // Kalles ETTER getRackAtPosition (reoler har prioritet ved overlapp).
  const getBinAtPosition = (screenX: number, screenY: number): PlacedBinRenderInfo | null => {
    if (!layout) return null;
    const world = screenToWorld(
      screenX, screenY,
      canvasSize.width, canvasSize.height,
      layout.dimensions, editorState.zoom, editorState.panOffset
    );

    for (const bin of binRenderInfos) {
      const rotated = (bin.rotation === 90 || bin.rotation === 270)
        ? { width: bin.depthM, depth: bin.widthM }
        : { width: bin.widthM, depth: bin.depthM };
      if (
        world.x >= bin.x - rotated.width / 2 &&
        world.x <= bin.x + rotated.width / 2 &&
        world.y >= bin.y - rotated.depth / 2 &&
        world.y <= bin.y + rotated.depth / 2
      ) {
        return bin;
      }
    }
    return null;
  };

  // Hit-test for objekter — kalles ETTER reoler og enkeltlokasjoner (lavest prioritet).
  const getObjectAtPosition = (screenX: number, screenY: number): WarehouseObject | null => {
    if (!layout) return null;
    const world = screenToWorld(
      screenX, screenY,
      canvasSize.width, canvasSize.height,
      layout.dimensions, editorState.zoom, editorState.panOffset
    );

    for (const obj of layout.objects) {
      const rotated = (obj.rotation === 90 || obj.rotation === 270)
        ? { width: obj.depth, depth: obj.width }
        : { width: obj.width, depth: obj.depth };
      if (
        world.x >= obj.x - rotated.width / 2 &&
        world.x <= obj.x + rotated.width / 2 &&
        world.y >= obj.y - rotated.depth / 2 &&
        world.y <= obj.y + rotated.depth / 2
      ) {
        return obj;
      }
    }
    return null;
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!layout) return;
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const screenX = e.clientX - rect.left;
    const screenY = e.clientY - rect.top;

    // Right-click or middle-click for panning
    if (e.button === 1 || e.button === 2) {
      setIsPanning(true);
      setPanStart({ x: e.clientX, y: e.clientY });
      e.preventDefault();
      return;
    }
    if (e.button !== 0) return;

    // Kalibreringsmodus: venstreklikk setter punkt A/B — all annen interaksjon er av
    if (state.calibration.active) {
      const world = screenToWorld(
        screenX, screenY,
        canvasSize.width, canvasSize.height,
        layout.dimensions, editorState.zoom, editorState.panOffset
      );
      dispatch({ type: 'SET_CALIBRATION_POINT', point: world });
      return;
    }

    // Flytte-modus: venstreklikk velger mål — all vanlig seleksjon/drag er av
    // (samme mønster som kalibrering). Kandidat-reol åpner popover med reolens
    // kandidat-bins; kandidat-enkeltlokasjon settes som mål direkte.
    if (moveActive) {
      const moveClickedRack = getRackAtPosition(screenX, screenY);
      if (moveClickedRack) {
        const candidateCount = moveCandidateRackCounts?.get(moveClickedRack.rackNumber);
        setMovePopover(
          candidateCount
            ? { rackNumber: moveClickedRack.rackNumber, x: screenX, y: screenY }
            : null
        );
        return;
      }
      const moveClickedBin = getBinAtPosition(screenX, screenY);
      if (moveClickedBin && moveCandidateBinSet?.has(moveClickedBin.binCode)) {
        setMovePopover(null);
        dispatch({ type: 'SET_MOVE_TARGET', binCode: moveClickedBin.binCode });
        return;
      }
      setMovePopover(null);
      return;
    }

    // Objektplasseringsmodus: venstreklikk plasserer et nytt objekt på world-pos
    // (snappet til rutenett) og forblir i modusen — plasser flere, Esc avslutter.
    if (state.objectPlacement) {
      const world = screenToWorld(
        screenX, screenY,
        canvasSize.width, canvasSize.height,
        layout.dimensions, editorState.zoom, editorState.panOffset
      );
      const objType = state.objectPlacement.type;
      const size = OBJECT_TYPE_DEFAULT_SIZES[objType];
      const sameTypeCount = layout.objects.filter(o => o.type === objType).length;
      dispatch({
        type: 'ADD_OBJECT',
        object: {
          id: generateObjectId(),
          type: objType,
          name: `${objectTypeLabel(t, objType)} ${sameTypeCount + 1}`,
          x: snapToGrid(world.x, layout.gridUnit),
          y: snapToGrid(world.y, layout.gridUnit),
          width: size.width,
          depth: size.depth,
          height: size.height,
          rotation: 0,
        },
      });
      return;
    }

    const clickedRack = getRackAtPosition(screenX, screenY);
    const isMultiSelect = isMultiSelectKey(e);
    const isRangeSelect_ = isRangeSelectKey(e);

    if (clickedRack) {
      let newSelection: Set<string>;
      if (isMultiSelect) {
        newSelection = toggleSelection(editorState.selectedRacks, clickedRack.rackNumber);
      } else if (isRangeSelect_ && lastClickedRack) {
        newSelection = rangeSelect(editorState.selectedRacks, layout.racks, lastClickedRack, clickedRack.rackNumber);
      } else {
        newSelection = singleSelect(clickedRack.rackNumber);
      }

      dispatch({ type: 'SET_SELECTION', selectedRacks: newSelection });
      setLastClickedRack(clickedRack.rackNumber);

      // Start drag
      if (newSelection.has(clickedRack.rackNumber)) {
        const world = screenToWorld(
          screenX, screenY,
          canvasSize.width, canvasSize.height,
          layout.dimensions, editorState.zoom, editorState.panOffset
        );
        const startPositions = new Map<string, { x: number; y: number }>();
        Array.from(newSelection).forEach(rackNum => {
          const rack = layout.racks.find(r => r.rackNumber === rackNum);
          if (rack) startPositions.set(rackNum, { x: rack.x, y: rack.y });
        });
        setDragState({
          isDragging: true,
          draggedRacks: Array.from(newSelection),
          startPositions,
          mouseStartX: world.x,
          mouseStartY: world.y,
        });
      }
    } else {
      // Ingen reol truffet — sjekk enkeltlokasjoner (reoler har prioritet)
      const clickedBin = getBinAtPosition(screenX, screenY);
      if (clickedBin) {
        dispatch({ type: 'SET_SELECTED_BIN', binCode: clickedBin.binCode });
        const world = screenToWorld(
          screenX, screenY,
          canvasSize.width, canvasSize.height,
          layout.dimensions, editorState.zoom, editorState.panOffset
        );
        setBinDragState({
          isDragging: true,
          binCode: clickedBin.binCode,
          startX: clickedBin.x,
          startY: clickedBin.y,
          mouseStartX: world.x,
          mouseStartY: world.y,
        });
      } else {
        // Ingen enkeltlokasjon truffet — sjekk objekter (lavest prioritet)
        const clickedObject = getObjectAtPosition(screenX, screenY);
        if (clickedObject) {
          dispatch({ type: 'SET_SELECTED_OBJECT', id: clickedObject.id });
          const world = screenToWorld(
            screenX, screenY,
            canvasSize.width, canvasSize.height,
            layout.dimensions, editorState.zoom, editorState.panOffset
          );
          setObjectDragState({
            isDragging: true,
            objectId: clickedObject.id,
            startX: clickedObject.x,
            startY: clickedObject.y,
            mouseStartX: world.x,
            mouseStartY: world.y,
          });
          return;
        }
        if (state.selectedBinCode) {
          dispatch({ type: 'SET_SELECTED_BIN', binCode: null });
        }
        if (editorState.selectedObject) {
          dispatch({ type: 'SET_SELECTED_OBJECT', id: null });
        }
        if (!isMultiSelect) {
          // Start rubber band selection
          dispatch({ type: 'SET_RUBBER_BAND', rubberBand: startRubberBand(screenX, screenY) });
          dispatch({ type: 'SET_SELECTION', selectedRacks: new Set() });
        }
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!layout) return;

    if (isPanning) {
      const dx = e.clientX - panStart.x;
      const dy = e.clientY - panStart.y;
      setPanStart({ x: e.clientX, y: e.clientY });
      dispatch({
        type: 'SET_PAN_OFFSET',
        offset: { x: editorState.panOffset.x + dx, y: editorState.panOffset.y + dy },
      });
      return;
    }

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const screenX = e.clientX - rect.left;
    const screenY = e.clientY - rect.top;

    // Kalibreringsmodus: oppdater «gummistrikk»-endepunkt, ingen annen interaksjon
    if (state.calibration.active) {
      if (state.calibration.pointA && !state.calibration.pointB) {
        const world = screenToWorld(
          screenX, screenY,
          canvasSize.width, canvasSize.height,
          layout.dimensions, editorState.zoom, editorState.panOffset
        );
        setCalibrationHover(world);
      }
      return;
    }

    // Flytte-modus: ingen drag/rubber band (panning håndteres over)
    if (moveActive) return;

    // Rubber band
    if (editorState.rubberBand.isActive) {
      dispatch({ type: 'SET_RUBBER_BAND', rubberBand: updateRubberBand(editorState.rubberBand, screenX, screenY) });

      const { startX, startY } = editorState.rubberBand;
      const world1 = screenToWorld(startX, startY, canvasSize.width, canvasSize.height, layout.dimensions, editorState.zoom, editorState.panOffset);
      const world2 = screenToWorld(screenX, screenY, canvasSize.width, canvasSize.height, layout.dimensions, editorState.zoom, editorState.panOffset);
      const selectedInArea = getRacksInArea(layout.racks, rackDimensionsMap, world1.x, world1.y, world2.x, world2.y);
      dispatch({ type: 'SET_SELECTION', selectedRacks: new Set(selectedInArea) });
      return;
    }

    // Drag av enkeltlokasjon
    if (binDragState.isDragging && binDragState.binCode) {
      const world = screenToWorld(
        screenX, screenY,
        canvasSize.width, canvasSize.height,
        layout.dimensions, editorState.zoom, editorState.panOffset
      );
      const deltaX = world.x - binDragState.mouseStartX;
      const deltaY = world.y - binDragState.mouseStartY;
      dispatch({
        type: 'MOVE_BIN',
        binCode: binDragState.binCode,
        x: snapToGrid(binDragState.startX + deltaX, layout.gridUnit),
        y: snapToGrid(binDragState.startY + deltaY, layout.gridUnit),
      });
      return;
    }

    // Drag av objekt (søyle/port/stasjon/lading/sonemarkør)
    if (objectDragState.isDragging && objectDragState.objectId) {
      const world = screenToWorld(
        screenX, screenY,
        canvasSize.width, canvasSize.height,
        layout.dimensions, editorState.zoom, editorState.panOffset
      );
      const deltaX = world.x - objectDragState.mouseStartX;
      const deltaY = world.y - objectDragState.mouseStartY;
      dispatch({
        type: 'MOVE_OBJECT',
        id: objectDragState.objectId,
        x: snapToGrid(objectDragState.startX + deltaX, layout.gridUnit),
        y: snapToGrid(objectDragState.startY + deltaY, layout.gridUnit),
      });
      return;
    }

    // Dragging
    if (dragState.isDragging && dragState.draggedRacks.length > 0) {
      const world = screenToWorld(
        screenX, screenY,
        canvasSize.width, canvasSize.height,
        layout.dimensions, editorState.zoom, editorState.panOffset
      );
      const deltaX = world.x - dragState.mouseStartX;
      const deltaY = world.y - dragState.mouseStartY;

      const updates = dragState.draggedRacks
        .map(rackNumber => {
          const startPos = dragState.startPositions.get(rackNumber);
          if (!startPos) return null;
          return {
            rackNumber,
            x: snapToGrid(startPos.x + deltaX, layout.gridUnit),
            y: snapToGrid(startPos.y + deltaY, layout.gridUnit),
          };
        })
        .filter((u): u is NonNullable<typeof u> => u !== null);

      dispatch({ type: 'MOVE_RACKS', updates });
    }
  };

  const handleMouseUp = () => {
    if (editorState.rubberBand.isActive) {
      dispatch({ type: 'SET_RUBBER_BAND', rubberBand: resetRubberBand() });
    }
    setDragState({ isDragging: false, draggedRacks: [], startPositions: new Map(), mouseStartX: 0, mouseStartY: 0 });
    setBinDragState(IDLE_BIN_DRAG);
    setObjectDragState(IDLE_OBJECT_DRAG);
    setIsPanning(false);
  };

  const handleWheel = (e: React.WheelEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    dispatch({ type: 'SET_ZOOM', zoom: Math.max(0.3, Math.min(5, editorState.zoom * delta)) });
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  // --- Drag-and-drop from sidebar ---
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (!layout || !canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const world = screenToWorld(
      e.clientX - rect.left,
      e.clientY - rect.top,
      canvasSize.width,
      canvasSize.height,
      layout.dimensions,
      editorState.zoom,
      editorState.panOffset
    );

    const rackNumber = e.dataTransfer.types.includes('text/plain') ? '__drag__' : '';
    if (!rackNumber) return;

    const snappedX = snapToGrid(world.x, layout.gridUnit);
    const snappedY = snapToGrid(world.y, layout.gridUnit);

    dispatch({
      type: 'SET_SIDEBAR_DRAG_PREVIEW',
      preview: { rackNumber: '?', x: snappedX, y: snappedY, rotation: 0, isValid: true },
    });
  }, [layout, canvasSize, editorState.zoom, editorState.panOffset, dispatch]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dispatch({ type: 'SET_SIDEBAR_DRAG_PREVIEW', preview: null });

    const rackNumber = e.dataTransfer.getData('text/plain');
    if (!rackNumber || !layout || !canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const world = screenToWorld(
      e.clientX - rect.left,
      e.clientY - rect.top,
      canvasSize.width,
      canvasSize.height,
      layout.dimensions,
      editorState.zoom,
      editorState.panOffset
    );

    const snappedX = snapToGrid(world.x, layout.gridUnit);
    const snappedY = snapToGrid(world.y, layout.gridUnit);

    dispatch({
      type: 'ADD_RACK',
      rack: { rackNumber, x: snappedX, y: snappedY, rotation: 0 },
    });
  }, [layout, canvasSize, editorState.zoom, editorState.panOffset, dispatch]);

  const handleDragLeave = useCallback(() => {
    dispatch({ type: 'SET_SIDEBAR_DRAG_PREVIEW', preview: null });
  }, [dispatch]);

  return (
    <div ref={containerRef} className="relative w-full h-full bg-gray-900 rounded-lg overflow-hidden">
      {/* Instruksjons-banner under kalibrering */}
      {state.calibration.active && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 px-3 py-1.5 bg-amber-500/90 text-gray-900 text-xs font-medium rounded-lg shadow-lg pointer-events-none">
          {!state.calibration.pointA
            ? t('manager.modules.warehouseLayout.calibration.clickPointA', {}, 'Kalibrering: klikk på et punkt med kjent avstand (A)')
            : !state.calibration.pointB
              ? t('manager.modules.warehouseLayout.calibration.clickPointB', {}, 'Klikk på det andre punktet (B) — Esc avbryter')
              : t('manager.modules.warehouseLayout.calibration.enterDistance', {}, 'Oppgi den virkelige avstanden')}
        </div>
      )}

      {/* Instruksjons-banner i objektplasseringsmodus */}
      {state.objectPlacement && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 px-3 py-1.5 bg-blue-600/95 text-white text-xs font-medium rounded-lg shadow-lg pointer-events-none whitespace-nowrap">
          {t(
            'manager.modules.warehouseLayout.objects.placeBanner',
            { '0': objectTypeLabel(t, state.objectPlacement.type) },
            `Plasser ${objectTypeLabel(t, state.objectPlacement.type).toLowerCase()}: klikk i kartet — Esc avslutter`
          )}
        </div>
      )}

      {/* Instruksjons-banner + filterpanel i flytte-modus */}
      {moveState && (
        <>
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 px-3 py-1.5 bg-emerald-600/95 text-white text-xs font-medium rounded-lg shadow-lg pointer-events-none whitespace-nowrap">
            {t(
              'manager.modules.warehouseLayout.move.banner',
              { '0': moveState.itemNo },
              `Flytt ${moveState.itemNo}: velg mållokasjon — Esc avbryter`
            )}
          </div>
          <MoveTargetFilterPanel
            filters={moveFilters}
            onFiltersChange={setMoveFilters}
            totalCandidates={moveTargets?.totalCandidates ?? null}
            truncated={moveTargets?.truncated ?? false}
            loading={moveTargetsLoading}
            error={moveTargetsError}
          />
        </>
      )}

      {/* Popover: kandidat-bins i valgt reol (flytte-modus) */}
      {moveState && movePopover && (
        <div
          className="absolute z-20 w-64 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden flex flex-col max-h-64"
          style={{
            left: Math.max(0, Math.min(movePopover.x, canvasSize.width - 264)),
            top: Math.max(0, Math.min(movePopover.y, canvasSize.height - 270)),
          }}
        >
          <div className="flex items-center justify-between px-3 py-1.5 bg-gray-50 border-b border-gray-200 flex-shrink-0">
            <span className="text-xs font-semibold text-gray-700 truncate">
              {t(
                'manager.modules.warehouseLayout.move.rackPopoverTitle',
                { '0': movePopover.rackNumber },
                `Kandidater i ${movePopover.rackNumber}`
              )}
            </span>
            <button
              onClick={() => setMovePopover(null)}
              className="p-0.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0"
              title={t('common.buttons.close', {}, 'Lukk')}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {movePopoverBins.map(bin => (
              <button
                key={bin.binCode}
                onClick={() => {
                  setMovePopover(null);
                  dispatch({ type: 'SET_MOVE_TARGET', binCode: bin.binCode });
                }}
                className="w-full px-3 py-1.5 text-left text-xs hover:bg-emerald-50 transition-colors border-b border-gray-100 last:border-b-0"
              >
                <span className="font-mono font-medium text-gray-900">{bin.binCode}</span>
                <span className="text-gray-500">
                  {' — '}V{bin.vertical ?? '?'}/H{bin.shelf ?? '?'}{' — '}
                  {bin.isEmpty
                    ? t('manager.modules.warehouseLayout.move.emptyBin', {}, 'tom')
                    : moveQuantityFormat.format(bin.currentQuantity)}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
      <canvas
        ref={canvasRef}
        width={canvasSize.width}
        height={canvasSize.height}
        className="cursor-crosshair"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
        onContextMenu={handleContextMenu}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onDragLeave={handleDragLeave}
      />
      <InsightLegend />
      {/* Soneoversikt øverst til høyre — monteres kun når togglet på (hent-ved-åpning) */}
      {state.showZonePanel && <ZoneSummaryPanel />}
      <CalibrationDialog />
    </div>
  );
};

export default Layout2DCanvas;
